// My Team's lineup as stats (B-413, PLAN-2026-10-18 §3): the lineup's
// spreadsheet switched from Fantasy to Stats, the box score of each player
// over a range — the season, the last three games, last week, the projection.
//
// A TABLE FOR EACH POSITION (B-514): each with its position's own columns
// at the reader's level (B-517), so no receiver's row reads a quarterback's
// passing as zeros. Each in the lineup's order — the starters, the bench,
// the reserve — with the slot leading the row: FLEX on the receiver who
// plays there. Until 2026-10-26 a table was a position group's, ESPN's My
// Team's way, the offense in one.
//
// THE POOL'S READ OF MY PLAYERS (`useMyStats`), so the lineup's own read
// carries no stats; a player the lineup has and that read does not (a move
// just made, read again in a moment) has dashes until it has.

import type { LineupRowView, PlayerRowView, StatRange } from '@fsp/domain';
import { failureOf, valueOf } from '@fsp/domain';
import { useAtLeast, useDetail } from '../../data/detail';
import { useMyStats } from '../../data/hooks';
import { EmptyState, positionLabel } from '../../kit';
import { Stack } from '../../kit/ui';
import { StatsTable, columnsAt, rangePointsTip } from '../shared/StatsTable';

export function MyStats({
  rows,
  range,
  short,
  onOpen,
  teamId,
}: {
  /** The lineup's rows in its order: starters, bench, reserve. */
  rows: readonly LineupRowView[];
  range: StatRange;
  short: boolean;
  onOpen: (r: LineupRowView) => void;
  /** Another team's lineup (D1, B-461): its players' stats; absent is mine. */
  teamId?: string;
}) {
  const read = useMyStats(range, teamId);
  const page = valueOf(read);
  const failure = failureOf(read);
  const standard = useAtLeast('standard');
  const level = useDetail();
  if (page === null)
    return <EmptyState>{failure !== null ? failure.message : 'Reading the stats…'}</EmptyState>;
  const byId = new Map<string, PlayerRowView>(page.items.map((r) => [r.player.id, r]));
  const filled = rows.filter((r) => r.player !== null);
  // NOBODY TO SHOW is said, never a blank panel (the review of B-461).
  if (filled.length === 0) return <EmptyState>Nobody on the roster to show.</EmptyState>;
  return (
    <Stack gap={3}>
      {page.statGroups.map((g) => {
        const own = filled.filter((r) => g.positions.includes(r.player!.positions[0] ?? ''));
        if (own.length === 0) return null;
        return (
          <StatsTable
            key={g.positions.join()}
            label={g.positions.map(positionLabel).join(', ')}
            rows={own.map((r) => ({
              key: `${r.slot}-${r.slotIndex}-${r.player!.id}`,
              player: r.player!,
              stats: byId.get(r.player!.id)?.stats ?? null,
              lead: <span className="fsp-stats-slot">{positionLabel(r.slot)}</span>,
              onOpen: () => onOpen(r),
            }))}
            columns={columnsAt(g.columns, level)}
            games={standard && range !== 'projected'}
            pointsTip={rangePointsTip(range, page.statWeek)}
            short={short}
          />
        );
      })}
    </Stack>
  );
}
